import { useState } from 'react'

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Area } from './Area'
import { Building } from './Building'
import { ChipComponent as Chip } from './chip'
import { Currency } from './Currency'
import { Floor } from './Floor'
import { FloorPlanButton } from './FloorPlanButton'
import { Rooms } from './Rooms'
import { getLocation, getReferrer } from './utils'
import { useCreateInterestSignup } from '../../api/widget'

const initialState = {
  organizationName: '',
  organizationNumber: '',
  fullName: '',
  email: '',
  phone: '',
  postalCode: '',
  birthYear: '',
  acceptPrivacyPolicy: false,
}

export const SignupDialog = ({
  fields,
  isOrg,
  onClose,
  onError,
  onSuccess,
  open,
  privacyPolicyHtml,
  property,
  token,
}) => {
  const { t } = useTranslation()
  const { projectId } = useParams()
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  const [state, setState] = useState(initialState)
  const [errors, setErrors] = useState({})
  const mutation = useCreateInterestSignup()

  const setField = (field) => (ev) => {
    setState({ ...state, [field]: ev.target.value })
    setErrors({ ...errors, [field]: false })
  }

  const handleClose = () => {
    setState(initialState)
    setErrors({})
    mutation.reset()
    onClose()
  }

  const handleSubmit = async (ev) => {
    ev.preventDefault()

    if (mutation.isPending) return

    if (!state.acceptPrivacyPolicy) {
      setErrors({ ...errors, acceptPrivacyPolicy: true })
      return
    }

    const data = {
      fullName: state.fullName,
      email: state.email,
      phone: state.phone,
      zip: state.postalCode,
      birthYear: state.birthYear,
      acceptPrivacyPolicy: state.acceptPrivacyPolicy,
      projectId,
      propertyId: property.id,
      location: getLocation(document),
      referrer: getReferrer(document),
      token,
    }

    if (isOrg) {
      data.organizationName = state.organizationName
      data.organizationNumber = state.organizationNumber
    }

    mutation.mutate(data, {
      onSuccess: () => {
        setState(initialState)
        onSuccess()
      },
      onError: (error) => {
        if (error.validationErrors) {
          setErrors({
            fullName: !!error.validationErrors.fullName,
            email: !!error.validationErrors.email,
            phone: !!error.validationErrors.phone,
            postalCode: !!error.validationErrors.postalCode,
            birthYear: !!error.validationErrors.birthYear,
            organizationName: !!error.validationErrors.organizationName,
            acceptPrivacyPolicy: !!error.validationErrors.acceptPrivacyPolicy,
          })
        } else {
          onError(error)
        }
      },
    })
  }

  return (
    <Dialog
      component="form"
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      onSubmit={handleSubmit}
      open={open}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          {t('Interest for {{n}}', { n: property?.number })}
          {fields?.includes('STATUS') && <Chip status={property?.status} />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {(fields?.includes('BUILDING') ||
                  fields?.includes('ROOMS') ||
                  fields?.includes('AREA') ||
                  fields?.includes('FLOOR')) && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Stack
                        direction="row"
                        spacing={1}
                        divider={<Divider orientation="vertical" flexItem />}
                      >
                        {fields?.includes('BUILDING') && (
                          <Building value={property?.building} />
                        )}
                        {fields?.includes('ROOMS') && (
                          <Rooms value={property?.rooms} />
                        )}
                        {fields?.includes('AREA') && (
                          <Area value={property?.area} />
                        )}
                        {fields?.includes('FLOOR') && (
                          <Floor value={property?.floor} />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                {fields?.includes('FLOOR_PLAN_URL') && (
                  <TableRow>
                    <TableCell>
                      <Typography>{t('Floor plan')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <FloorPlanButton url={property?.floorPlanUrl} />
                    </TableCell>
                  </TableRow>
                )}
                {fields?.includes('PRICE') && (
                  <TableRow>
                    <TableCell>
                      <Typography>{t('Price')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Currency value={property?.price} />
                    </TableCell>
                  </TableRow>
                )}
                {fields?.includes('MONTHLY_FEE') && (
                  <TableRow>
                    <TableCell>
                      <Typography>{t('Monthly fee')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Currency value={property?.monthlyFee} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid2 container spacing={2}>
            {isOrg && (
              <>
                <Grid2
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <TextField
                    fullWidth
                    label={t('Organization name')}
                    name="organization"
                    onChange={setField('organizationName')}
                    value={state.organizationName}
                    helperText={
                      errors.organizationName && t('Enter organization name')
                    }
                  />
                </Grid2>
              </>
            )}
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label={t('Name')}
                name="name"
                onChange={setField('fullName')}
                value={state.fullName}
                helperText={errors.fullName && t('Enter name')}
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label={t('Email address')}
                name="email"
                onChange={setField('email')}
                value={state.email}
                helperText={errors.email && t('Enter email address')}
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <TextField
                fullWidth
                label={t('Phone number')}
                name="tel"
                onChange={setField('phone')}
                value={state.phone}
                helperText={errors.phone && t('Enter phone number')}
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <TextField
                fullWidth
                label={t('Postal code')}
                name="postal-code"
                onChange={setField('postalCode')}
                value={state.postalCode}
                helperText={errors.postalCode && t('Enter postal code')}
              />
            </Grid2>
            {!isOrg && (
              <Grid2
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <TextField
                  fullWidth
                  label={t('Year of birth')}
                  name="bday-year"
                  onChange={setField('birthYear')}
                  value={state.birthYear}
                  helperText={
                    errors.birthYear && t('Enter year of birth (yyyy)')
                  }
                />
              </Grid2>
            )}
          </Grid2>
          <FormControl error={!!errors.acceptPrivacyPolicy}>
            <FormControlLabel
              checked={state.acceptPrivacyPolicy}
              onChange={(ev) => {
                setState({ ...state, acceptPrivacyPolicy: ev.target.checked })
                setErrors({ ...errors, acceptPrivacyPolicy: false })
              }}
              control={<Checkbox />}
              label={
                <span
                  className="html-content"
                  dangerouslySetInnerHTML={{ __html: privacyPolicyHtml || '' }}
                />
              }
            />
            <FormHelperText>
              {errors.acceptPrivacyPolicy &&
                t('Please accept our personal data policy')}
            </FormHelperText>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button loading={mutation.isPending} variant="contained" type="submit">
          {t('Send')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
